import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { SimpleModeProvider } from "@/contexts/SimpleModeContext";
import { DataFetchStrategyProvider } from "@/contexts/DataFetchStrategyContext";
import { GlobalLoadingProvider } from "@/components/global/GlobalLoading";
import { ConfirmDialogProvider } from "@/components/global/ConfirmDialog";
import { ErrorBoundary } from "@/components/routing/ErrorBoundary";
import { ProtectedRoute } from "@/components/routing/ProtectedRoute";
import { InstallPrompt } from "@/components/global/InstallPrompt";
import { UpdateBanner } from "@/components/global/UpdateBanner";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { CameraScreen } from "./screens/CameraScreen";
import { ScoreScreen } from "./screens/ScoreScreen";
import { NetworkScreen } from "./screens/NetworkScreen";
import { ActivityLogScreen } from "./screens/ActivityLogScreen";
import { SettingsScreen } from "./screens/SettingsScreen";
import { AuthScreen } from "./screens/AuthScreen";
import { SatelliteScreen } from "./screens/SatelliteScreen";
import { USSDScreen } from "./screens/USSDScreen";
import { AnalyticsScreen } from "./screens/AnalyticsScreen";
import { AgriAgentScreen } from "./screens/AgriAgentScreen";
import { PastQuestionsScreen } from "./screens/PastQuestionsScreen";
import { ConversationDetailScreen } from "./screens/ConversationDetailScreen";
import { KnowledgeScreen } from "./screens/KnowledgeScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { OnboardingFlow } from "./features/onboarding/OnboardingFlow";
import { AAKConsentJourney } from "./features/consent/AAKConsentJourney";
import { MicroInteractionsShowcase } from "./components/dev/MicroInteractionsShowcase";
import { getLanguageFont, isRTL } from "./i18n/config";

// CBS Quick Action Screens
import { MortalityLogScreen } from "./screens/MortalityLogScreen";
import { FeedLogScreen } from "./screens/FeedLogScreen";
import { VaccinationLogScreen } from "./screens/VaccinationLogScreen";
import { BiosecurityLogScreen } from "./screens/BiosecurityLogScreen";
import { DailyRecordScreen } from "./screens/DailyRecordScreen";
import { BatchDetailScreen } from "./screens/BatchDetailScreen";
import { NewBatchScreen } from "./screens/NewBatchScreen";
import { SettlementViewScreen } from "./screens/SettlementViewScreen";
import { SupervisorVisitScreen } from "./screens/SupervisorVisitScreen";
import { SupervisorDashboardScreen } from "./screens/SupervisorDashboardScreen";
import { VisitHistoryScreen } from "./screens/VisitHistoryScreen";
import { BatchHistoryScreen } from "./screens/BatchHistoryScreen";
import { PhotosScreen } from "./screens/PhotosScreen";
// Lazy loaded financial screens with retry
function lazyRetry(importFn: () => Promise<any>) {
  return lazy(() => importFn().catch(() => {
    return new Promise(resolve => {
      setTimeout(() => resolve(importFn()), 1500);
    });
  }));
}

const MoneyScreen = lazyRetry(() => import("./screens/MoneyScreen").then(m => ({ default: m.MoneyScreen })));
const BanksScreen = lazyRetry(() => import("./screens/BanksScreen").then(m => ({ default: m.BanksScreen })));
const LoansScreen = lazyRetry(() => import("./screens/LoansScreen").then(m => ({ default: m.LoansScreen })));
const PartnerScreen = lazyRetry(() => import("./screens/PartnerScreen").then(m => ({ default: m.PartnerScreen })));

const queryClient = new QueryClient();

const AppContent = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const fontClass = getLanguageFont(i18n.language);
    document.documentElement.className = fontClass;
    // Set RTL direction for Urdu and other RTL languages
    document.documentElement.dir = isRTL(i18n.language) ? 'rtl' : 'ltr';
  }, [i18n.language]);

  return (
    <>
      <UpdateBanner />
      <Toaster />
      <Sonner />
      <InstallPrompt />
      
      <Routes>
        {/* Public routes */}
        <Route path="/auth" element={<AuthScreen />} />
        <Route path="/onboarding" element={<OnboardingFlow />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Index />
          </ProtectedRoute>
        } />
        <Route path="/camera" element={
          <ProtectedRoute>
            <CameraScreen />
          </ProtectedRoute>
        } />
        <Route path="/score" element={
          <ProtectedRoute>
            <ScoreScreen />
          </ProtectedRoute>
        } />
        <Route path="/network" element={
          <ProtectedRoute>
            <NetworkScreen />
          </ProtectedRoute>
        } />
        <Route path="/activity" element={
          <ProtectedRoute>
            <ActivityLogScreen />
          </ProtectedRoute>
        } />
        <Route path="/activity/:id" element={
          <ProtectedRoute>
            <ActivityLogScreen />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsScreen />
          </ProtectedRoute>
        } />
        <Route path="/satellite" element={
          <ProtectedRoute>
            <SatelliteScreen />
          </ProtectedRoute>
        } />
        <Route path="/ussd" element={
          <ProtectedRoute>
            <USSDScreen />
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <AnalyticsScreen />
          </ProtectedRoute>
        } />
        <Route path="/agent" element={
          <ProtectedRoute>
            <AgriAgentScreen />
          </ProtectedRoute>
        } />
        <Route path="/agent/history" element={
          <ProtectedRoute>
            <PastQuestionsScreen />
          </ProtectedRoute>
        } />
        <Route path="/agent/history/:conversationId" element={
          <ProtectedRoute>
            <ConversationDetailScreen />
          </ProtectedRoute>
        } />
        <Route path="/knowledge" element={
          <ProtectedRoute>
            <KnowledgeScreen />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={<Navigate to="/settings" replace />} />
        <Route path="/consent" element={
          <ProtectedRoute>
            <AAKConsentJourney />
          </ProtectedRoute>
        } />
        <Route path="/photos" element={
          <ProtectedRoute>
            <PhotosScreen />
          </ProtectedRoute>
        } />
        
        {/* CBS Quick Action Routes */}
        <Route path="/log/mortality" element={
          <ProtectedRoute>
            <MortalityLogScreen />
          </ProtectedRoute>
        } />
        <Route path="/log/feed" element={
          <ProtectedRoute>
            <FeedLogScreen />
          </ProtectedRoute>
        } />
        <Route path="/log/vaccination" element={
          <ProtectedRoute>
            <VaccinationLogScreen />
          </ProtectedRoute>
        } />
        <Route path="/log/biosecurity" element={
          <ProtectedRoute>
            <BiosecurityLogScreen />
          </ProtectedRoute>
        } />
        <Route path="/daily-record" element={
          <ProtectedRoute>
            <DailyRecordScreen />
          </ProtectedRoute>
        } />
        <Route path="/batch/new" element={
          <ProtectedRoute>
            <NewBatchScreen />
          </ProtectedRoute>
        } />
        <Route path="/batch/history" element={
          <ProtectedRoute>
            <BatchHistoryScreen />
          </ProtectedRoute>
        } />
        <Route path="/batch/:batchId" element={
          <ProtectedRoute>
            <BatchDetailScreen />
          </ProtectedRoute>
        } />
        <Route path="/settlement/:settlementId" element={
          <ProtectedRoute>
            <SettlementViewScreen />
          </ProtectedRoute>
        } />
        
        {/* Financial routes */}
        <Route path="/money" element={
          <ProtectedRoute>
            <ErrorBoundary fallback={<div className="flex items-center justify-center h-screen"><p>Failed to load. <a href="/money">Retry</a></p></div>}>
              <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                <MoneyScreen />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/banks" element={
          <ProtectedRoute>
            <ErrorBoundary fallback={<div className="flex items-center justify-center h-screen"><p>Failed to load. <a href="/banks">Retry</a></p></div>}>
              <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                <BanksScreen />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/loans" element={
          <ProtectedRoute>
            <ErrorBoundary fallback={<div className="flex items-center justify-center h-screen"><p>Failed to load. <a href="/loans">Retry</a></p></div>}>
              <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                <LoansScreen />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/partner" element={
          <ProtectedRoute>
            <ErrorBoundary fallback={<div className="flex items-center justify-center h-screen"><p>Failed to load. <a href="/partner">Retry</a></p></div>}>
              <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                <PartnerScreen />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        {/* Supervisor/Field Officer routes */}
        <Route path="/supervisor" element={
          <ProtectedRoute>
            <SupervisorDashboardScreen />
          </ProtectedRoute>
        } />
        <Route path="/supervisor/history/:farmerId" element={
          <ProtectedRoute>
            <VisitHistoryScreen />
          </ProtectedRoute>
        } />
        <Route path="/visit" element={
          <ProtectedRoute>
            <SupervisorVisitScreen />
          </ProtectedRoute>
        } />
        
        {/* Dev routes */}
        <Route path="/dev/interactions" element={<MicroInteractionsShowcase />} />
        
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <DataFetchStrategyProvider>
          <SimpleModeProvider>
            <BrowserRouter>
              <AuthProvider>
                <GlobalLoadingProvider>
                  <ConfirmDialogProvider>
                    <TooltipProvider>
                      <AppContent />
                    </TooltipProvider>
                  </ConfirmDialogProvider>
                </GlobalLoadingProvider>
              </AuthProvider>
            </BrowserRouter>
          </SimpleModeProvider>
        </DataFetchStrategyProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
